import React, { useState, useCallback, useEffect } from "react";
import Axios from "axios";
import { useNavigate } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import prenatal from "../../../Icons/Avtar/PreConception.png";
import useContactAnalytics from "./useContactAnalytics";
import { ContactGeneralMeta } from "./ContactSeoMeta";

import {
  Section, Inner, BgBlob, DotGrid,
  HeroContent, HeroEyebrow, HeroTitle, HeroDivider, HeroDesc,
  FormBlock, FormLayout,
  SidePanel, SidePanelImg, SidePanelOverlay, SidePanelMeta,
  PanelEyebrow, PanelTitle, PanelAccent, PanelContact,
  FormContent, FormTitle, FormSubtitle,
  FieldGrid, FieldWrap, FieldLabel, InputField, SelectField,
  FieldError, FieldHint,
  SubmitBtn, SpinnerRing,
  SuccessWrap, SuccessIcon, SuccessTitle, SuccessDesc,
  ContactLine, DynamicFields,
} from "./ContactUs.styles";

// ─── Constants ─────────────────────────────────────────────────────────────────

const API_URL =
  "https://us-central1-website-a1336.cloudfunctions.net/api/registerVisitor";

const SALUTATION_OPTIONS = ["Mr.", "Mrs.", "Ms.", "Dr.", "Prof."];
const JOB_TITLE_OPTIONS  = [
  "Student",
  "Yoga Trainer",
  "Yoga Academy",
  "Corporate Yoga",
  "Healthcare Professional",
  "Other",
];

const INITIAL_FORM = {
  salutation: "",
  name:       "",
  email:      "",
  country:    "",
  mobile:     "",
  jobTitle:   "",
};

// ─── Icons ────────────────────────────────────────────────────────────────────

const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8a19.79 19.79 0 01-3.07-8.67A2 2 0 012 .82h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 8.75a16 16 0 006.29 6.29l1.29-1.29a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7a2 2 0 011.72 2.02z" />
  </svg>
);

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// ─── ContactUsForm ────────────────────────────────────────────────────────────

const ContactUsForm = () => {
  const navigate  = useNavigate();
  const analytics = useContactAnalytics();

  const [formData,  setFormData]  = useState(INITIAL_FORM);
  const [errors,    setErrors]    = useState({});
  const [loading,   setLoading]   = useState(false);
  const [showModal, setShowModal] = useState(false);

  // ── Form view on mount ──
  useEffect(() => {
    analytics.trackFormView("general", window.location.href);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Validation ──
  const validate = useCallback((data) => {
    const e = {};
    if (!data.salutation)                                    e.salutation = "Required";
    if (!data.name.trim())                                   e.name       = "Full name is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email))     e.email      = "Valid email required";
    if (!data.country.trim())                                e.country    = "Country code required";
    if (!/^\+?[\d\s-]{7,15}$/.test(data.mobile))            e.mobile     = "Valid phone required";
    if (!data.jobTitle)                                      e.jobTitle   = "Please select a role";
    return e;
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev)    => ({ ...prev, [name]: undefined }));
  }, []);

  const handleModalClose = useCallback(() => {
    setShowModal(false);
    navigate("/");
  }, [navigate]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const errs = validate(formData);
    const hasErrors = Object.keys(errs).length > 0;

    analytics.trackSubmitAttempt("general", hasErrors);
    if (hasErrors) { setErrors(errs); return; }

    try {
      setLoading(true);
      const payload = {
        name:       formData.name,
        email:      formData.email,
        mobile:     formData.mobile,
        salutation: formData.salutation,
        job_title:  formData.jobTitle,
        source:     "Yoga-Shastra",
      };
      const res = await Axios.post(API_URL, payload);
      if (res.data.code === "NP001") {
        analytics.trackSubmitSuccess("general", { job_title: formData.jobTitle });
        setShowModal(true);
        setFormData(INITIAL_FORM);
        setTimeout(handleModalClose, 5000);
      }
    } catch (err) {
      console.error("Form submission error:", err);
      analytics.trackSubmitError("general", "network");
    } finally {
      setLoading(false);
    }
  }, [formData, validate, handleModalClose, analytics]);

  return (
    <>
      <Section id="contact">

        {/* ── SEO — ContactGeneralMeta mirrors BlogListMeta pattern ── */}
        <ContactGeneralMeta />

        <BgBlob className="b1" aria-hidden="true" />
        <BgBlob className="b2" aria-hidden="true" />
        <BgBlob className="b3" aria-hidden="true" />
        <DotGrid aria-hidden="true" />

        {/* ── Hero header ── */}
        <HeroContent>
          <HeroEyebrow>✦ Get In Touch</HeroEyebrow>
          <HeroTitle>Drop Us a Line</HeroTitle>
          <HeroDivider aria-hidden="true" />
          <HeroDesc>
            Ready to begin your yoga journey? Fill in your details and one of
            our experienced instructors will reach out to guide you toward the
            perfect practice for your goals.
          </HeroDesc>
        </HeroContent>

        <Inner>
          <FormBlock $delay={0.1}>
            <FormLayout>

              {/* ── Left image panel ── */}
              <SidePanel>
                <SidePanelImg src={prenatal} alt="Yoga practice" loading="lazy" />
                <SidePanelOverlay aria-hidden="true" />
                <SidePanelMeta>
                  <PanelEyebrow>✦ Contact Us</PanelEyebrow>
                  <PanelTitle>Start Your Yoga Journey Today</PanelTitle>
                  <PanelAccent aria-hidden="true" />
                  <PanelContact
                    href="tel:+917838866331"
                    aria-label="Call us"
                    onClick={() => analytics.trackPhoneClick("general", "panel")}
                  >
                    <PhoneIcon />
                    +91-78388 66331
                  </PanelContact>
                </SidePanelMeta>
              </SidePanel>

              {/* ── Right form ── */}
              <FormContent>
                <FormTitle>Tell us about yourself</FormTitle>
                <FormSubtitle>
                  All fields marked * are required. We'll get back to you within 24 hours.
                </FormSubtitle>

                <form onSubmit={handleSubmit} noValidate>
                  {/* Row 1: Salutation + Full Name */}
                  <FieldGrid $cols={2}>
                    <FieldWrap>
                      <FieldLabel htmlFor="salutation">Salutation *</FieldLabel>
                      <SelectField
                        id="salutation"
                        name="salutation"
                        value={formData.salutation}
                        onChange={handleChange}
                        onFocus={() => analytics.trackFieldFocus("salutation", "general")}
                        $error={!!errors.salutation}
                        aria-required="true"
                        aria-describedby={errors.salutation ? "sal-err" : undefined}
                      >
                        <option value="" disabled>Select…</option>
                        {SALUTATION_OPTIONS.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </SelectField>
                      {errors.salutation && (
                        <FieldError id="sal-err" role="alert">{errors.salutation}</FieldError>
                      )}
                    </FieldWrap>

                    <FieldWrap>
                      <FieldLabel htmlFor="name">Full Name *</FieldLabel>
                      <InputField
                        id="name" type="text" name="name"
                        placeholder="e.g. Aarav Sharma"
                        value={formData.name}
                        onChange={handleChange}
                        onFocus={() => analytics.trackFieldFocus("name", "general")}
                        $error={!!errors.name}
                        aria-required="true"
                        aria-describedby={errors.name ? "name-err" : undefined}
                      />
                      {errors.name && (
                        <FieldError id="name-err" role="alert">{errors.name}</FieldError>
                      )}
                    </FieldWrap>
                  </FieldGrid>

                  {/* Row 2: Email */}
                  <FieldGrid $cols={1}>
                    <FieldWrap>
                      <FieldLabel htmlFor="email">Email Address *</FieldLabel>
                      <InputField
                        id="email" type="email" name="email"
                        placeholder="you@example.com"
                        value={formData.email}
                        onChange={handleChange}
                        onFocus={() => analytics.trackFieldFocus("email", "general")}
                        $error={!!errors.email}
                        aria-required="true"
                      />
                      {errors.email && (
                        <FieldError role="alert">{errors.email}</FieldError>
                      )}
                    </FieldWrap>
                  </FieldGrid>

                  {/* Row 3: Country code + Mobile */}
                  <FieldGrid $cols={2}>
                    <FieldWrap>
                      <FieldLabel htmlFor="country">Country Code *</FieldLabel>
                      <InputField
                        id="country" type="text" name="country"
                        placeholder="+91"
                        value={formData.country}
                        onChange={handleChange}
                        onFocus={() => analytics.trackFieldFocus("country", "general")}
                        $error={!!errors.country}
                        aria-required="true"
                      />
                      {errors.country && (
                        <FieldError role="alert">{errors.country}</FieldError>
                      )}
                    </FieldWrap>

                    <FieldWrap>
                      <FieldLabel htmlFor="mobile">Mobile Number *</FieldLabel>
                      <InputField
                        id="mobile" type="tel" name="mobile"
                        placeholder="88888 00000"
                        value={formData.mobile}
                        onChange={handleChange}
                        onFocus={() => analytics.trackFieldFocus("mobile", "general")}
                        $error={!!errors.mobile}
                        aria-required="true"
                      />
                      {errors.mobile && (
                        <FieldError role="alert">{errors.mobile}</FieldError>
                      )}
                    </FieldWrap>
                  </FieldGrid>

                  {/* Row 4: Role */}
                  <FieldGrid $cols={1}>
                    <FieldWrap>
                      <FieldLabel htmlFor="jobTitle">I am a *</FieldLabel>
                      <SelectField
                        id="jobTitle" name="jobTitle"
                        value={formData.jobTitle}
                        onChange={handleChange}
                        onFocus={() => analytics.trackFieldFocus("jobTitle", "general")}
                        $error={!!errors.jobTitle}
                        aria-required="true"
                      >
                        <option value="" disabled>Select your role…</option>
                        {JOB_TITLE_OPTIONS.map((j) => (
                          <option key={j} value={j}>{j}</option>
                        ))}
                      </SelectField>
                      {errors.jobTitle && (
                        <FieldError role="alert">{errors.jobTitle}</FieldError>
                      )}
                    </FieldWrap>
                  </FieldGrid>

                  <SubmitBtn
                    type="submit"
                    disabled={loading}
                    aria-busy={loading}
                    aria-label="Submit contact form"
                  >
                    {loading
                      ? <><SpinnerRing aria-hidden="true" /> Submitting…</>
                      : <>Submit <ArrowIcon /></>
                    }
                  </SubmitBtn>
                </form>

                <ContactLine>
                  <PhoneIcon aria-hidden="true" />
                  Or call us directly at{" "}
                  <a
                    href="tel:+917838866331"
                    aria-label="Call +91 78388 66331"
                    onClick={() => analytics.trackPhoneClick("general", "footer_line")}
                  >
                    +91-78388 66331
                  </a>
                </ContactLine>
              </FormContent>
            </FormLayout>
          </FormBlock>
        </Inner>
      </Section>

      {/* ── Success modal ── */}
      <Modal
        show={showModal}
        onHide={handleModalClose}
        centered
        backdrop="static"
        aria-labelledby="success-modal-title"
      >
        <Modal.Body style={{ padding: 0 }}>
          <SuccessWrap>
            <SuccessIcon>
              <CheckIcon />
            </SuccessIcon>
            <SuccessTitle id="success-modal-title">Message Received!</SuccessTitle>
            <SuccessDesc>
              <strong>Thank you for contacting us.</strong>
              <br />
              One of our representatives will get in touch with you shortly.
            </SuccessDesc>
          </SuccessWrap>
        </Modal.Body>
      </Modal>
    </>
  );
};

ContactUsForm.displayName = "ContactUsForm";
export default React.memo(ContactUsForm);